import type { BreadcrumbTrail } from './breadcrumbs'
import type { RequestIdTrail } from './request-ids'

export interface FetchObserver {
  uninstall(): void
}

interface Options {
  requestIds: RequestIdTrail
  breadcrumbs: BreadcrumbTrail
}

/** The path, without origin or query string — the query can carry credentials. */
function pathOf(input: RequestInfo | URL): string {
  const raw =
    typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
  try {
    return new URL(raw, window.location.origin).pathname
  } catch {
    return raw
  }
}

function methodOf(input: RequestInfo | URL, init: RequestInit | undefined): string {
  if (init?.method) return init.method.toUpperCase()
  if (typeof input !== 'string' && !(input instanceof URL) && input.method) {
    return input.method.toUpperCase()
  }
  return 'GET'
}

let installed = false

/**
 * Observes API traffic to feed the correlation and breadcrumb trails.
 *
 * Wrapping `fetch` rather than the API client is deliberate: the app reaches the
 * backend three ways — the `@revalize/api-client` hook, the `api-fetch` escape
 * hatch, and raw `fetch` — and only the shared primitive underneath sees all of
 * them. Uploads that use XHR directly are the known gap.
 *
 * The wrapper is transparent. It never changes a response, never swallows a
 * rejection, and records nothing that would keep a failed request from reaching
 * the code that asked for it.
 */
export function installFetchObserver(options: Options): FetchObserver {
  const { requestIds, breadcrumbs } = options

  if (installed) {
    // Double-wrapping would record every call twice, which reads as duplicated
    // traffic rather than as a bug.
    return { uninstall: (): void => {} }
  }

  const original = globalThis.fetch
  installed = true

  globalThis.fetch = async function observedFetch(
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> {
    const path = pathOf(input)
    const method = methodOf(input, init)

    let response: Response
    try {
      response = await original(input as RequestInfo, init)
    } catch (error) {
      // The request never completed, so there is no id to correlate — but the
      // fact that it failed is exactly the context a report needs.
      breadcrumbs.record({ action: 'api.network_error', target: `${method} ${path}` })
      throw error
    }

    try {
      requestIds.recordResponse({ headers: response.headers, status: response.status, method, path })
      // Only failures leave a breadcrumb. Every successful call would crowd out
      // the handful of entries that explain what went wrong.
      if (response.status >= 400) {
        breadcrumbs.record({
          action: 'api.error',
          target: `${method} ${path} → ${response.status}`,
        })
      }
    } catch {
      // Observation must never be the reason a request appears to fail.
    }

    return response
  } as typeof fetch

  return {
    uninstall: (): void => {
      globalThis.fetch = original
      installed = false
    },
  }
}
